/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  Asset,
  AssetUi,
  BookmarkUtils,
  Channel,
  ContentProps,
  Episode,
  Minutes,
  Program,
  Seconds,
  SquareCard,
  translate,
  AssetUtils,
  Ducks,
} from '@wit/vtv-x-foundation-lib';
// import UiConstants from '../../ui/common/Constants';
import ParentalControl from '../core/ParentalControl';

export default class AssetUiUtils {
  static toChannelCard(label: string, channel: Channel): Channel {
    return { ...channel, label };
  }

  static isSquareCard(obj: AssetUi): obj is SquareCard {
    return (obj as SquareCard).link !== undefined;
  }

  static getAssetUiWithProgress(assets: AssetUi[], bookmarks: Ducks.BookmarksCache.ReducerState): AssetUi[] {
    if (Array.isArray(assets) && assets.length) {
      return assets.map((asset) => {
        const assetBookmark = bookmarks[(asset as Asset).id]?.data;
        let progress;
        if (assetBookmark) {
          progress = BookmarkUtils.getProgressFromBookmark(asset as ContentProps, assetBookmark);
        }
        return {
          ...asset,
          progress,
        };
      });
    }
    return assets;
  }

  static getAssetUiWithAgeRatingUpdated(assets: AssetUi[]): AssetUi[] {
    if (Array.isArray(assets) && assets.length) {
      return assets.map((asset) => {
        const rating = ParentalControl.UI.getAssetParentalRating(asset as Asset);
        if (rating) {
          return {
            ...asset,
            attribution: { ageRating: rating.toString() },
          };
        }
        return asset;
      });
    }
    return assets;
  }

  static buildSeasonEpisodeInfo(asset: Asset, includeSeparator: boolean): string | undefined {
    let infoResult = '';

    const separator = ` '\u00b7' `;

    if (includeSeparator) {
      infoResult = separator;
    }

    let season;
    let episode;

    if (AssetUtils.hasValidSeasonNumber(asset)) {
      season = (asset as Episode | Program).seasonNumber;
      infoResult = `${infoResult}${translate['pi.seasonInitial']}${season}`;
    }

    if (AssetUtils.hasValidEpisodeNumber(asset)) {
      episode = (asset as Episode | Program).episodeNumber;
      if (season) {
        infoResult = `${infoResult} `;
      }
      infoResult = `${infoResult}${translate['pi.episodeInitial']}${episode}`;
    }

    return infoResult !== '' && infoResult !== separator ? infoResult : undefined;
  }

  static remainingTimeAsString(remainingTime: Seconds): string {
    // 1h 55m, 30m, 55s
    const date = new Date(0);
    date.setSeconds(remainingTime);
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const seconds = date.getUTCSeconds();

    let remainingTimeFormat = translate.formatString(translate['pi.remainingTime.seconds'], seconds).toString();

    if (hours > 0) {
      remainingTimeFormat = translate.formatString(translate['pi.remainingTime.hours'], hours, minutes).toString();
    } else if (minutes > 0) {
      remainingTimeFormat = translate.formatString(translate['pi.remainingTime.minutes'], minutes).toString();
    }

    return translate.formatString(translate['pi.remainingTime'], remainingTimeFormat).toString();
  }

  static cardDurationString(durationMinutes: Minutes): string {
    // 1h 55m, 30m
    const date = new Date(0);
    date.setMinutes(durationMinutes);
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();

    return hours > 0
      ? translate.formatString(translate['card.duration.hours'], hours, minutes).toString()
      : translate.formatString(translate['card.duration.minutes'], minutes).toString();
  }

  static rentDurationInDays(duration: Minutes): number {
    return Math.round(duration / 60 / 24);
  }
}
